import React, { useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';

const NavLink = ({ to, children, isActive }) => {
  const linkRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const link = linkRef.current;
    const glow = glowRef.current;

    const handleMouseEnter = () => {
      gsap.to(glow, {
        opacity: 1,
        scale: 1.2,
        duration: 0.4,
        ease: 'power2.out',
      });
      gsap.to(link, {
        color: '#f4d03f',
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(glow, {
        opacity: 0,
        scale: 1,
        duration: 0.4,
        ease: 'power2.out',
      });
      gsap.to(link, {
        color: isActive ? '#d4af37' : '#c0c0c0',
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    // Magnetic movement toward cursor
    const handleMouseMove = (e) => {
      const rect = link.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const deltaX = (e.clientX - centerX) * 0.15;
      const deltaY = (e.clientY - centerY) * 0.15;

      gsap.to(link, {
        x: deltaX,
        y: deltaY,
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseLeaveReset = () => {
      gsap.to(link, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'elastic.out(1, 0.5)',
      });
    };

    link.addEventListener('mouseenter', handleMouseEnter);
    link.addEventListener('mouseleave', handleMouseLeave);
    link.addEventListener('mousemove', handleMouseMove);
    link.addEventListener('mouseleave', handleMouseLeaveReset);

    return () => {
      link.removeEventListener('mouseenter', handleMouseEnter);
      link.removeEventListener('mouseleave', handleMouseLeave);
      link.removeEventListener('mousemove', handleMouseMove);
      link.removeEventListener('mouseleave', handleMouseLeaveReset);
    };
  }, [isActive]);

  return (
    <div className="relative">
      {/* Glow effect behind link */}
      <div
        ref={glowRef}
        className="absolute inset-0 rounded-full opacity-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.4) 0%, transparent 70%)',
          filter: 'blur(10px)',
          transform: 'scale(2)',
        }}
      />
      <Link
        ref={linkRef}
        to={to}
        className="relative px-6 py-3 text-lg font-display tracking-widest uppercase transition-colors"
        style={{
          color: isActive ? '#d4af37' : '#c0c0c0',
          textShadow: isActive ? '0 0 20px rgba(212, 175, 55, 0.5)' : 'none',
          willChange: 'transform, color',
        }}
      >
        {children}
        {isActive && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute -bottom-1 left-1/2 w-8 h-0.5 bg-cosmic-gold"
            style={{
              transform: 'translateX(-50%)',
              boxShadow: '0 0 10px rgba(212, 175, 55, 0.8)',
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        )}
      </Link>
    </div>
  );
};

const Navigation = () => {
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    // Entrance animation
    gsap.fromTo(
      navRef.current,
      { opacity: 0, y: -30 },
      { opacity: 1, y: 0, duration: 1.5, delay: 1, ease: 'power3.out' }
    );
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <motion.nav
      ref={navRef}
      className="fixed top-0 left-0 right-0 z-50 px-8 py-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 1 }}
    >
      <div
        className="max-w-7xl mx-auto flex items-center justify-between"
        style={{
          background: 'linear-gradient(180deg, rgba(10, 10, 15, 0.8) 0%, transparent 100%)',
        }}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 70%)',
              border: '1px solid rgba(212, 175, 55, 0.4)',
              boxShadow: '0 0 20px rgba(212, 175, 55, 0.2)',
            }}
          >
            <span className="text-2xl" style={{ color: '#d4af37' }}>☉</span>
          </div>
          <div className="flex flex-col">
            <span
              className="text-lg font-display tracking-wider"
              style={{
                color: '#d4af37',
                textShadow: '0 0 30px rgba(212, 175, 55, 0.3)',
              }}
            >
              ஸ்ரீ செல்வகணபதி
            </span>
            <span
              className="text-xs font-display tracking-[0.2em] uppercase"
              style={{
                color: 'rgba(212, 175, 55, 0.7)',
              }}
            >
              Jothidam
            </span>
          </div>
        </Link>

        {/* Navigation Links */}
        <div className="flex items-center gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              isActive={location.pathname === item.path}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;
